import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Badge } from 'react-bootstrap';
import { Table } from '@rd-web-markets/shared/dist/util';
import { EditButton, DeleteButton, SaveButton, CancelButton } from '@rd-web-markets/shared/dist/util/buttons';
import { createTimezonedDate } from '@rd-web-markets/shared/dist/util/date/dateUtils';
const NOTE_TYPE_LABELS = {
  summary: 'Summary',
  comments: 'Comments',
  project_notes: 'Project Notes'
};
const NOTE_TYPE_BADGE_COLORS = {
  summary: 'primary',
  comments: 'success',
  project_notes: 'warning'
};
const ReportNotesTable = _ref => {
  var _formData$note2;
  let {
    notes = [],
    onUpdate,
    onDelete,
    userCanEdit,
    loading,
    filterByType = null,
    showTypeColumn = true,
    currentUserId,
    setHasUnsavedChanges
  } = _ref;
  const {
    t
  } = useTranslation();
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [originalNoteText, setOriginalNoteText] = useState('');
  const getNoteTypeLabel = type => {
    return NOTE_TYPE_LABELS[type] || type;
  };
  const getNoteTypeBadgeColor = type => {
    return NOTE_TYPE_BADGE_COLORS[type] || 'secondary';
  };
  const handleEdit = note => {
    setEditingNoteId(note.id);
    setOriginalNoteText(note.note || '');
    setFormData({
      note: note.note || ''
    });
  };
  const handleCancel = () => {
    setEditingNoteId(null);
    setFormData({});
    setOriginalNoteText('');
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };
  const handleChange = value => {
    setFormData({
      note: value
    });
  };
  useEffect(() => {
    if (setHasUnsavedChanges && editingNoteId !== null) {
      var _formData$note;
      const hasChanges = ((_formData$note = formData.note) === null || _formData$note === void 0 ? void 0 : _formData$note.trim()) !== originalNoteText.trim();
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData.note, originalNoteText, editingNoteId, setHasUnsavedChanges]);
  const handleSave = async note => {
    setSaving(true);
    await onUpdate({
      id: note.id,
      data: formData
    });
    setEditingNoteId(null);
    setFormData({});
    setOriginalNoteText('');
    setSaving(false);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };
  const handleDelete = async note => {
    if (window.confirm(t('confirm_delete'))) {
      await onDelete(note.id);
    }
  };
  const canSave = ((_formData$note2 = formData.note) === null || _formData$note2 === void 0 ? void 0 : _formData$note2.trim()) !== '';
  const extractDate = date => {
    return createTimezonedDate(date).toCustomLocaleDateString();
  };
  const filteredNotes = useMemo(() => {
    if (!filterByType) return notes;
    return notes.filter(note => note.note_type === filterByType);
  }, [notes, filterByType]);
  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [filteredNotes]);
  const headers = useMemo(() => {
    const baseHeaders = [{
      text: t('date'),
      width: '12%'
    }, {
      text: t('user'),
      width: '15%'
    }, {
      text: t('note'),
      width: showTypeColumn ? '45%' : '60%'
    }];
    if (showTypeColumn) {
      baseHeaders.push({
        text: t('type'),
        width: '15%'
      });
    }
    baseHeaders.push({
      text: '',
      width: '13%'
    });
    return baseHeaders;
  }, [t, showTypeColumn]);
  const rows = useMemo(() => {
    return sortedNotes.map(note => {
      const isEditing = editingNoteId === note.id;
      const isSavingThisRow = saving && isEditing;
      const isOwner = currentUserId && note.user_id === currentUserId;
      const columns = isEditing ? [/*#__PURE__*/React.createElement("small", null, extractDate(note.created_at)), /*#__PURE__*/React.createElement("small", null, note.user_name), /*#__PURE__*/React.createElement(Form.Control, {
        as: "textarea",
        rows: 3,
        size: "sm",
        name: "note",
        value: formData.note,
        onChange: e => handleChange(e.target.value),
        placeholder: t('type_a_comment')
      }), ...(showTypeColumn ? [/*#__PURE__*/React.createElement(Badge, {
        bg: getNoteTypeBadgeColor(note.note_type)
      }, getNoteTypeLabel(note.note_type))] : []), /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(SaveButton, {
        size: "sm",
        onClick: () => handleSave(note),
        disabled: isSavingThisRow || !canSave,
        text: isSavingThisRow ? t('saving') : t('save')
      }), /*#__PURE__*/React.createElement(CancelButton, {
        size: "sm",
        onClick: handleCancel,
        disabled: isSavingThisRow
      }))] : [/*#__PURE__*/React.createElement("small", null, extractDate(note.created_at)), /*#__PURE__*/React.createElement("small", null, note.user_name), /*#__PURE__*/React.createElement("div", {
        className: "bg-secondary p-2 rounded"
      }, /*#__PURE__*/React.createElement("small", null, note.note)), ...(showTypeColumn ? [/*#__PURE__*/React.createElement(Badge, {
        bg: getNoteTypeBadgeColor(note.note_type)
      }, getNoteTypeLabel(note.note_type))] : []), userCanEdit && isOwner ? /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(EditButton, {
        size: "sm",
        onClick: () => handleEdit(note)
      }), /*#__PURE__*/React.createElement(DeleteButton, {
        size: "sm",
        onClick: () => handleDelete(note)
      })) : null];
      return {
        key: "".concat(note.id, "-").concat(isEditing ? 'edit' : 'view'),
        note: note,
        columns: columns
      };
    });
  }, [sortedNotes, editingNoteId, formData, userCanEdit, saving, t, showTypeColumn, currentUserId]);
  if (sortedNotes.length === 0) {
    return /*#__PURE__*/React.createElement("div", {
      className: "text-center py-4"
    }, /*#__PURE__*/React.createElement("p", {
      className: "text-muted mb-0"
    }, t('no_notes_yet')));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "report-notes-table"
  }, /*#__PURE__*/React.createElement(Table, {
    headers: headers,
    rows: rows,
    showControls: false,
    opts: {
      bordered: false,
      striped: true,
      responsive: true,
      hover: true,
      headerSticky: true
    },
    style: {
      borderCollapse: 'collapse'
    }
  }));
};
export default ReportNotesTable;